import {useAui} from '@assistant-ui/react';
import {
  Banner,
  Button,
  CalloutIcon,
  DotProgress,
  Icon,
} from '@wandb/components';
import {clsx} from 'clsx';
import React, {useCallback, useMemo, useState} from 'react';
import {shallowEqual} from 'react-redux';
import {Virtuoso} from 'react-virtuoso';

import {useStoreSelector} from '../../../agentChat/store/context';
import {
  selectActiveThread,
  selectHistoryItems,
  selectIsOnNewChat,
  selectThreadDisplayTitle,
} from '../../../agentChat/store/selectors';
import type {AgentChatState, Thread} from '../../../agentChat/store/thread';
import {useNavContext} from '../../NavContextProvider';
import {useNewChat} from '../useNewChat';
import {useTrackWBAgentChats} from '../useTrackWBAgent';
import {groupItemsByTimeBucket} from './grouping';
import {HistoryItem, type HistoryItemMode} from './HistoryItem';
import {useThreadHistoryContext} from './ThreadHistoryProvider';
import {useDeleteThread} from './useDeleteThread';
import {useRenameThread} from './useRenameThread';

type Row = string | Thread;

type PendingAction = {threadId: string; type: 'rename' | 'delete'} | null;

interface VirtuosoContext {
  status: 'idle' | 'loading' | 'error';
  loadMore: () => void;
}

export function WBAgentChatHistorySidebar() {
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);

  const aui = useAui();
  const {params} = useNavContext();
  const currentEntity = params.entityName ?? null;
  const renameThread = useRenameThread();
  const deleteThread = useDeleteThread();
  const handleNewChat = useNewChat();
  const {trackChatSwitched, trackChatRenamed, trackChatDeleted} =
    useTrackWBAgentChats();

  const {ids, status, loadInitial, loadMore} = useThreadHistoryContext();

  const historyItems = useStoreSelector(
    useCallback((s: AgentChatState) => selectHistoryItems(s, ids), [ids]),
    shallowEqual
  );

  const displayTitles = useStoreSelector(
    useCallback(
      (s: AgentChatState) =>
        Object.fromEntries(
          ids.map(id => [id, selectThreadDisplayTitle(s, id)])
        ),
      [ids]
    ),
    shallowEqual
  );
  const hasItems = historyItems.length > 0;

  const activeThreadId = useStoreSelector(
    useCallback((s: AgentChatState) => selectActiveThread(s)?.id ?? null, [])
  );
  const isOnNewChat = useStoreSelector(selectIsOnNewChat);

  const handleSelect = useCallback(
    (rootTurnId: string) => {
      aui.threads().switchToThread(rootTurnId);
      trackChatSwitched(rootTurnId);
    },
    [aui, trackChatSwitched]
  );

  const handleStartRename = useCallback((threadId: string) => {
    setPendingAction({threadId, type: 'rename'});
  }, []);

  const handleCommitRename = useCallback(
    (threadId: string, newTitle: string) => {
      setPendingAction(null);
      void renameThread(threadId, newTitle);
      trackChatRenamed(threadId);
    },
    [renameThread, trackChatRenamed]
  );

  const handleStartDelete = useCallback((threadId: string) => {
    setPendingAction({threadId, type: 'delete'});
  }, []);

  const handleConfirmDelete = useCallback(
    (threadId: string) => {
      setPendingAction(null);
      void deleteThread(threadId);
      trackChatDeleted(threadId);
    },
    [deleteThread, trackChatDeleted]
  );

  const handleCancelMode = useCallback(() => {
    setPendingAction(null);
  }, []);

  const rows = useMemo<Row[]>(() => {
    const groups = groupItemsByTimeBucket(
      historyItems,
      thread => new Date(thread.createdAt),
      new Date()
    );
    return [...groups].flatMap(([label, groupItems]) => [label, ...groupItems]);
  }, [historyItems]);

  const itemContent = useCallback(
    (index: number) => {
      const row = rows[index];
      // A bucket label from 'groupItemsByTimeBucket'.
      if (typeof row === 'string') {
        return (
          <div
            className={clsx(
              'h-[32px] py-[4px] px-[4px] flex items-center justify-between text-base font-semibold text-moon-800',
              index > 0 && 'mt-16'
            )}>
            {row}
          </div>
        );
      }

      const mode: HistoryItemMode =
        pendingAction?.threadId === row.id
          ? pendingAction.type === 'rename'
            ? 'renaming'
            : 'confirming-delete'
          : 'idle';

      return (
        <HistoryItem
          thread={row}
          displayTitle={displayTitles[row.id]}
          currentEntity={currentEntity}
          mode={mode}
          isRunning={row.status === 'running'}
          isSelected={row.id === activeThreadId}
          onSelect={handleSelect}
          onStartRename={handleStartRename}
          onCommitRename={handleCommitRename}
          onStartDelete={handleStartDelete}
          onConfirmDelete={handleConfirmDelete}
          onCancelMode={handleCancelMode}
        />
      );
    },
    [
      rows,
      displayTitles,
      currentEntity,
      activeThreadId,
      pendingAction,
      handleSelect,
      handleStartRename,
      handleCommitRename,
      handleStartDelete,
      handleConfirmDelete,
      handleCancelMode,
    ]
  );

  const virtuosoContext = useMemo<VirtuosoContext>(
    () => ({status, loadMore}),
    [status, loadMore]
  );

  const isEmpty = !hasItems && status === 'idle';
  const isInitialError = !hasItems && status === 'error';
  const isInitialLoading = !hasItems && status === 'loading';

  return (
    <div className="flex flex-col w-[250px] min-h-0 border-r border-solid border-moon-250">
      <div className="flex flex-1 flex-col min-h-0 py-[6px] overflow-hidden text-moon-800">
        <div className="flex flex-col py-16 items-start gap-16 self-stretch">
          <div className="flex px-8 items-start gap-6 self-stretch">
            {!isInitialLoading && !isInitialError && (
              <button
                type="button"
                className={clsx(
                  'flex items-center self-stretch px-8 py-6 gap-8 h-full w-full cursor-pointer rounded-[4px] border-0 text-left font-[inherit]',
                  isOnNewChat
                    ? 'bg-teal-300/[0.32] text-teal-600 font-semibold'
                    : 'hover:bg-moon-100 text-moon-650 font-normal'
                )}
                onClick={handleNewChat}>
                <Icon icon="compose" aria-label="new chat" />
                <span className="text-[15px] leading-6">New chat</span>
              </button>
            )}
          </div>
        </div>
        {isInitialLoading && (
          <div className="flex items-center justify-center py-32">
            <DotProgress aria-label="Loading history" />
          </div>
        )}
        {isEmpty && (
          <div className="flex flex-col items-center justify-center gap-8 py-32 text-moon-500">
            <Icon icon="forum-chat-bubble" size="xl" />
            <span className="text-sm">No previous conversations</span>
          </div>
        )}
        {isInitialError && (
          <div className="flex flex-col justify-center items-center py-32 px-16 gap-12">
            <CalloutIcon icon="warning" color="red" size="md" />
            <span className="font-semibold text-base">
              Failed to load conversations
            </span>
            <Button
              aria-label="Retry loading conversations"
              onPress={loadInitial}
              startIcon="reload-refresh"
              variant="secondary">
              Retry
            </Button>
          </div>
        )}
        {hasItems && (
          <Virtuoso
            aria-label="Past conversations"
            components={virtuosoComponents}
            context={virtuosoContext}
            endReached={loadMore}
            itemContent={itemContent}
            overscan={50}
            style={{
              height: '100%',
              scrollbarGutter: 'stable',
              scrollbarWidth: 'thin',
              scrollbarColor: '#0d0f1252 transparent',
            }}
            totalCount={rows.length}
          />
        )}
      </div>
    </div>
  );
}

function VirtuosoFooter({context}: {context?: VirtuosoContext}) {
  if (context?.status === 'loading') {
    return (
      <div className="flex items-center justify-center py-8">
        <DotProgress aria-label="Loading more" />
      </div>
    );
  }

  if (context?.status === 'error') {
    return (
      <div className="w-full px-10 py-8">
        <Banner
          aria-label="Failed to load more conversations"
          isDismissible={false}
          isVisible
          size="md"
          variant="error">
          <Banner.Body>Failed to load more.</Banner.Body>
          <Banner.Action
            aria-label="Retry loading more conversations"
            onPress={context.loadMore}>
            Retry
          </Banner.Action>
        </Banner>
      </div>
    );
  }

  return null;
}

const VirtuosoList = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function VirtuosoList({children, style, ...props}, ref) {
  return (
    <div ref={ref} {...props} className="px-[6px]" style={style}>
      {children}
    </div>
  );
});

const virtuosoComponents = {
  Footer: VirtuosoFooter,
  List: VirtuosoList,
};
